import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import useReactRouter from "use-react-router";
import gql from "graphql-tag";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import { MTableToolbar } from "material-table";
import { CircularProgress, Typography } from "@material-ui/core";

import { useMe } from "../../reducers/me";
import UserModel from "../../model/user";
import Table from "../../components/Table";
import GatewayModel from "../../model/gateway";
import useSearchQuery from "../../lib/search-query";
import data from "../../lib/data";
import { useSnackbar } from "notistack";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { renderBuildingAddress, renderTimeFromLastSending } from "../../lib/texts";
import GatewayStatusLabel from "../../components/Gateway/GatewayStatusLabel";

const GATEWAY_SEARCH = gql`
  mutation gatewaysBacklogSearch(
    $query: SearchQuery!
    $gatewaysBacklogQuery: GatewaysBacklogSearchQuery
  ) {
    gatewaysBacklogSearch(
      query: $query
      gatewaysBacklogQuery: $gatewaysBacklogQuery
    ) {
      rows {
        id
        imei
        serial_number
        manufacturer
        model
        import_method
        status
        organization {
          id
          name
        }
        building {
          id
          external_id
          street
          house_number
          city
          postalcode
        }
        supervision {
          imei
          battery
          rssi
          operator
        }
        gateway_stats {
          status
          last_data_sending
        }
      }
      count
    }
  }
`;

const GET_ORGANIZATIONS = gql`
  query getOrganizations {
    organizations {
      id
      name
    }
  }
`;

const UPDATE_GATEWAY = gql`
  mutation updateGateway($id: ID!, $organizationId: ID) {
    updateGateway(id: $id, organizationId: $organizationId) {
      id
    }
  }
`;

export default function GatewaysAll({ match }) {
  const {
    data: organizationsData,
    loading: organizationsLoading,
    error: organizationsError,
  } = useQuery(GET_ORGANIZATIONS, {});

  const [updateGateway] = useMutation(UPDATE_GATEWAY, {
    onCompleted: () => {
      enqueueSnackbar(t("gateway:Gateway updated successfully"), {
        variant: "success",
      });
    },
    onError: (err) => {
      enqueueSnackbar(t("gateway:Could not save the gateway"), {
        variant: "error",
      });
    },
  });
  const [gatewaySearch] = useMutation(GATEWAY_SEARCH);
  const [pageSize, setPageSize] = useState(10);

  const query = useSearchQuery();
  const { t } = useTranslation(["gateway"]);
  const [me] = useMe();
  const { history } = useReactRouter();

  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const refreshInProgressTimoutRef = useRef(null);

  const [csvError, setCsvError] = useState([]);
  const [csvWarnings, setCsvWarnings] = useState([]);

  useEffect(() => {
    return () => {
      // clear timeout when unmounting
      clearTimeout(refreshInProgressTimoutRef.current);
    };
  });
  const tableRef = useRef();
  useEffect(() => {
    tableRef.current && tableRef.current.onQueryChange();
    setRefreshInProgress(false);
  }, [refreshInProgress]);

  const [searchTextChanged, setSearchTextChanged] = useState(null);
  // to prevent remounting toolbar after entering something into search field

  const Toolbar = useMemo(
    () => (props) =>
    (
      <MTableToolbar {...props} />
    ), []
  );

  if (refreshInProgress || organizationsLoading) {
    return <CircularProgress></CircularProgress>;
  }

  const columns = [
    {
      title: t("gateway:ID"),
      field: "id",
      type: "numeric",
      defaultSort: "desc",
      editable: "never",
    },
    {
      title: t('Status'),
      field: 'gateway_stats.status',
      filter: {
        fields: ['$GatewayStats.status$'],
      },
      association: 'GatewayStats',
      editable: false,
      render: (rowData) => {
        const status = rowData?.gateway_stats?.status;
        return <GatewayStatusLabel status={status} />;
      },
    },
    { title: t("Manufacturer"), field: "manufacturer", editable: false },
    { title: t("Serial Number"), field: "serial_number", editable: false },
    { title: t("IMEI"), field: "imei", editable: false },
    {
      title: t("Import Method"),
      field: "import_method",
      editable: false,
      render: (rowData) =>
        t(GatewayModel.GATEWAY_IMPORT_METHOD_VALUES[rowData.import_method]),
    },
    {
      title: t('Last Reading'),
      field: 'last_reading',
      type: 'datetime',
      editable: false,
      render: (rowData) => renderTimeFromLastSending(rowData.last_reading),
    },
    {
      title: t('Last Sending'),
      field: 'gateway_stats.last_data_sending',
      type: 'datetime',
      editable: 'never',
      filter: {
        fields: ['$GatewayStats.last_data_sending$'],
      },
      association: 'GatewayStats',
      render: (rowData) => renderTimeFromLastSending(rowData.gateway_stats?.last_data_sending),
    },
    {
      title: t('Building ID1'),
      field: 'building.external_id',
      filter: {
        fields: ['$Building.external_id$'],
      },
      association: 'Building',
      editable: false,
    },
    {
      title: t('Building Address'),
      field: 'buildingAddress',
      render: (rowData) => renderBuildingAddress(rowData),
      editable: false,
    },
    {
      title: t("Organization"),
      field: "organization.id",
      editable: "onUpdate",
      // initialEditValue:(rowData) => rowData.organization.id,
      lookup: organizationsData.organizations.reduce((acc, org) => {
        acc[Number(org.id)] = org.name;
        return acc;
      }, {}),
      render: (rowData) => {
        const organization = organizationsData.organizations.find(
          (org) => Number(org.id) === Number(rowData.organization?.id)
        );
        return organization ? organization.name : rowData.organization?.name || "";
      },
    },
  ];
  const columnFieldToColumnMap = columns.reduce(
    (result, column) => ({ ...result, [column.field]: column }),
    {}
  );

  const handleRowUpdate = async (newData, oldData) => {
    if (oldData["organization"]?.id !== newData["organization"]?.id) {
      await updateGateway({
        variables: {
          id: oldData.id,
          organizationId: newData["organization"]?.id,
        },
      });
    }
  };

  return (
    <React.Fragment>
      <Box
        hidden={csvWarnings && csvWarnings.length === 0}
        style={{
          maxHeight: 300,
          overflow: "scroll",
          padding: 5,
        }}
      >
        <h4>
          <IconButton
            onClick={() => setCsvWarnings([])}
            color="error"
            aria-label="Close warnings"
            component="span"
          >
            <CloseIcon />
          </IconButton>
          CSV Warnings:
        </h4>
        {csvWarnings &&
          csvWarnings.map((error) => (
            <Alert severity="warning" style={{ marginBottom: 3 }}>
              {error}
            </Alert>
          ))}

      </Box>
      <Box
        hidden={csvError && csvError.length === 0}
        style={{
          maxHeight: 300,
          overflow: "scroll",
          padding: 5,
        }}
      >
        <h4>
          <IconButton
            onClick={() => setCsvError([])}
            color="error"
            aria-label="Close errors"
            component="span"
          >
            <CloseIcon />
          </IconButton>
          CSV Errors:
        </h4>
        {csvError &&
          csvError.map((error) => (
            <Alert severity="error" style={{ marginBottom: 3 }}>
              {error}
            </Alert>
          ))}
      </Box>
      <Table
        tableId="gatewaysAll"
        title=""
        tableRef={tableRef}
        columns={columns}
        data={data(
          columnFieldToColumnMap,
          gatewaySearch,
          (response) => response.data.gatewaysBacklogSearch,
          setPageSize,
          [],
          false,
          {
            gatewaysBacklogQuery: {
              status: null,
              import_method: null,
              includeUnknownGateways: true,
            },
          }
        )}
        onSearchChange={(searchedText) => {
          // state change is needed to update unknown
          // organization gateways that match search text
          setSearchTextChanged(searchedText);
        }}
        options={{
          sorting: true,
          pageSize,
          searchAutoFocus: true,
          columnsButton: true,
          compressed: true,
          searchText: query.get(GatewayModel.buildingAddress),
        }}
        components={{
          Toolbar,
        }}
        onRowClick={(event, rowData) => {
          history.push(`${match.path}/${rowData.id}`);
        }}
        editable={
          UserModel.roleUser(me.role) && {
            isEditable: (rowData) => UserModel.roleAdmin(me.role) || !rowData.organization?.id,
            onRowUpdate: handleRowUpdate,
          }
        }
      />
    </React.Fragment>
  );
}
